import { LeaveMeetingDialog } from './components/LeaveMeetingDialog';
import { RoomAudioRenderer, RoomContext } from "@livekit/components-react";
import "@livekit/components-styles";
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { BottomControlBar } from "./components/BottomControlBar";
import { MeetingChatPanel } from "./components/MeetingChatPanel";
import { ControlPanel } from "./components/ControlPanel";
import { LiveKitProvider, useLiveKit } from "./contexts";
import DialogLink from "../meetings/components/DialogLink";
import {
  useMeetingConnection,
  useMeetingUI,
  useRoomDisconnect,
  useMeetingLeave,
} from "./hooks";
import {
  MeetingLoadingState,
  MeetingErrorState,
  JoinMeetingView,
  VideoConference,
} from "./components";
import useMqttClient from "@/hooks/useMqttClient";
import { isLockScreenPayload } from "./types/mqttMessages";

export default function LiveKit() {
  return (
    <LiveKitProvider>
      <LiveKitContent />
    </LiveKitProvider>
  );
}

function LiveKitContent() {
  const { user } = useAuth();
  const { meetingCode } = useParams < { meetingCode: string } > ();
  const [mainScreenFocus, setMainScreenFocus] = useState < string | null > (null);
  const [mainScreenType, setMainScreenType] = useState < "camera" | "screen_share" | null > (null);
  const {
    room,
    isConnecting,
    meeting,
    error,
    connectToMeeting,
    refreshMeeting,
    updateParticipantsFromMqtt,
    updateLockActionFromMqtt,
    isHostOrCoHost,
  } = useLiveKit();
  console.log("isHostOrCoHost", isHostOrCoHost);

  const {
    isChatOpen,
    isControlPanelOpen,
    showJoinDialog,
    isLinkMeetingOpen,
    setShowJoinDialog,
    setIsLinkMeetingOpen,
    handleToggleChat,
    handleToggleControlPanel,
    handleCloseChat,
    handleCloseControlPanel,
  } = useMeetingUI();

  const { handleJoinMeeting } = useMeetingConnection(
    meetingCode,
    () => setShowJoinDialog(true)
  );

  // MQTT Client
  const {
    connectionStatus,
    subscribe,
    subscribedTopics,
    messages,
  } = useMqttClient();

  useEffect(() => {
    if (connectionStatus && meeting?.id) subscribe(`tx/meeting/${meeting.id}`);
  }, [connectionStatus, meeting?.id]);

  const seenMessages = useRef(new WeakSet<object>());
  useEffect(() => {
    for (const envelope of messages) {
      if (seenMessages.current.has(envelope)) continue;
      seenMessages.current.add(envelope);
      try {
        const data = typeof envelope.payload === 'string' ? JSON.parse(envelope.payload) : envelope.payload;
        if (!meeting || (data.Message?.IdMeet ?? data.Message?.MeetingId) !== meeting.id) continue;
        if (isLockScreenPayload(data)) updateLockActionFromMqtt(data.Enable);
        if (data.Id === 41 && data.Message) {
          updateParticipantsFromMqtt({ userHands: data.Message.UserHands, userHosts: data.Message.UserHosts, userShares: data.Message.UserShares });
          void refreshMeeting();
        }
      } catch (error) { console.warn('Bản tin cuộc họp không hợp lệ', error); }
    }
  }, [messages, meeting?.id, refreshMeeting, updateParticipantsFromMqtt, updateLockActionFromMqtt]);
  useEffect(() => {
    setMainScreenFocus(meeting?.focusParticipant || null);
    const source = meeting?.mainType as string | undefined;
    setMainScreenType(source === 'SCREEN' || source === 'screen_share' ? 'screen_share' : source === 'CAM' || source === 'camera' ? 'camera' : null);
  }, [meeting?.focusParticipant, meeting?.mainType]);

  useRoomDisconnect(room);
  const { handleLeave, leaveDialogOpen, setLeaveDialogOpen, leaving, performLeave } = useMeetingLeave();

  useEffect(() => {
    if (error && !isConnecting && !room) {
      setShowJoinDialog(true);
    }
  }, [error, isConnecting, room, setShowJoinDialog]);

  const handleJoinMeetingWithClose = async (
    name: string,
    userId: string
  ): Promise<boolean> => {
    const success = await handleJoinMeeting(name, userId);
    if (success) {
      setShowJoinDialog(false);
    }
    return success;
  };

  if (showJoinDialog) {
    return (
      <JoinMeetingView
        showJoinDialog={showJoinDialog}
        onOpenChange={setShowJoinDialog}
        onJoin={handleJoinMeetingWithClose}
        meetingCode={meetingCode || ""}
        error={error}
      />
    );
  }

  if (isConnecting) {
    return <MeetingLoadingState />;
  }

  if (!room) {
    return <MeetingLoadingState message="Đang khởi tạo..." />;
  }

  if (error) {
    return (
      <MeetingErrorState
        error={error}
        onRetry={() =>
          connectToMeeting(
            meetingCode || "",
            user?.id || "",
            user?.fullName || ""
          )
        }
      />
    );
  }
  return (
    <RoomContext.Provider value={room}>
      <div
        data-lk-theme="default"
        className="flex h-screen bg-[#0f0f0f] overflow-hidden"
      >
        <div className="flex-1 flex flex-col relative min-w-0">
          <div className="flex-1 relative bg-black flex min-h-0">
            <VideoConference
              room={room}
              mainScreenFocus={mainScreenFocus}
              mainScreenType={mainScreenType}
              onMainScreenFocusChange={setMainScreenFocus}
            />
            <MeetingChatPanel
              room={room}
              currentUserName={user?.fullName || ""}
              isOpen={isChatOpen}
              onClose={handleCloseChat}
            />
          </div>

          <BottomControlBar
            room={room}
            onToggleChat={handleToggleChat}
            onToggleControlPanel={handleToggleControlPanel}
            onLeave={handleLeave}
            isHost={isHostOrCoHost}
            onLinkMeeting={() => setIsLinkMeetingOpen(true)}
            meeting={meeting}
          />
        </div>

        <ControlPanel
          isOpen={isControlPanelOpen}
          onClose={handleCloseControlPanel}
        />

        <LeaveMeetingDialog open={leaveDialogOpen} busy={leaving} onOpenChange={setLeaveDialogOpen}
          onLeave={()=>void performLeave(false)} onEnd={()=>void performLeave(true)}/>
        <RoomAudioRenderer />
        <DialogLink
          open={isLinkMeetingOpen}
          onOpenChange={setIsLinkMeetingOpen}
          link={meeting?.meetingCode || ""}
        />
      </div>
    </RoomContext.Provider>
  );
}
